import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  ChevronDown,
  Calendar,
  Download,
  Filter,
  FileText,
  FileSpreadsheet,
  BarChart3,
  Package,
  RefreshCw,
  SortAsc,
  Grid,
  Hash,
  Database,
  CheckCircle,
  ChevronUp,
  ChevronRight
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, branchesAPI, departmentAPI } from '../services/api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  format,
  parseISO,
  isSameDay,
  addHours,
} from 'date-fns';
import { arSA, enUS } from 'date-fns/locale';

// ==============================
// === INTERFACES ===
// ==============================
interface DailySummaryData {
  id: string;
  code?: string;
  name: string;
  nameAr?: string;
  nameEn?: string;
  totalQuantity: number;
  branchQuantities: { [branch: string]: number };
  productCode?: string;
  orderCount?: number;
  receivedQuantity?: number;
}

interface Branch {
  _id: string;
  name: string;
  nameEn?: string;
  displayName: string;
  code?: string;
}

interface Department {
  _id: string;
  name: string;
  nameEn?: string;
  code?: string;
}

interface QuickStats {
  totalOrders: number;
  receivedOrders: number;
  totalQuantity: number;
  receivedQuantity: number;
  period: string;
}

interface ReportTotals {
  grandTotalQuantity: number;
  totalProducts: number;
  totalBranches: number;
  totalOrders: number;
  totalReceivedOrders: number;
}

interface GroupByOption {
  value: string;
  labelAr: string;
  labelEn: string;
}

interface SortOption {
  value: string;
  labelAr: string;
  labelEn: string;
}

interface BranchData {
  [key: string]: number;
}

// ==============================
// === CONSTANTS ===
// ==============================
const PERIOD_OPTIONS = [
  { value: 'today', labelAr: 'اليوم', labelEn: 'Today' },
  { value: 'yesterday', labelAr: 'أمس', labelEn: 'Yesterday' },
  { value: 'week', labelAr: 'آخر 7 أيام', labelEn: 'Last 7 Days' },
  { value: 'month', labelAr: 'آخر 30 يوم', labelEn: 'Last 30 Days' },
  { value: 'custom', labelAr: 'فترة مخصصة', labelEn: 'Custom Range' },
];

const GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: 'product', labelAr: 'حسب المنتج', labelEn: 'By Product' },
  { value: 'department', labelAr: 'حسب القسم', labelEn: 'By Department' },
];

const SORT_OPTIONS: SortOption[] = [
  { value: 'code_asc', labelAr: 'الكود (أ-ي)', labelEn: 'Code (A-Z)' },
  { value: 'code_desc', labelAr: 'الكود (ي-أ)', labelEn: 'Code (Z-A)' },
  { value: 'name_asc', labelAr: 'الاسم (أ-ي)', labelEn: 'Name (A-Z)' },
  { value: 'name_desc', labelAr: 'الاسم (ي-أ)', labelEn: 'Name (Z-A)' },
  { value: 'quantity_desc', labelAr: 'الكمية (الأكبر أولاً)', labelEn: 'Quantity (High to Low)' },
  { value: 'quantity_asc', labelAr: 'الكمية (الأصغر أولاً)', labelEn: 'Quantity (Low to High)' },
];

// ==============================
// === UTILITY FUNCTIONS ===
// ==============================
const formatNumberWithComma = (num: number): string => {
  if (isNaN(num) || num === null || num === undefined) return '0';
  
  // التعامل مع الكسور - الاحتفاظ بكسرين عشريين إذا كان الرقم يحتوي على كسور
  if (num % 1 !== 0) {
    // تقريب إلى منزلتين عشريتين
    const rounded = Math.round(num * 100) / 100;
    const parts = rounded.toString().split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimalPart = parts[1] ? parts[1].padEnd(2, '0').substring(0, 2) : '00';
    return `${integerPart}.${decimalPart}`;
  }
  
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const toArabicNumerals = (number: string | number): string => {
  if (number === null || number === undefined) return '0';
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  
  // التعامل مع الأرقام الكسرية
  const numberStr = String(number);
  const parts = numberStr.split('.');
  
  // تحويل الجزء الصحيح
  const integerPart = parts[0].replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
  
  // تحويل الجزء العشري إذا وجد
  if (parts[1]) {
    const decimalPart = parts[1].replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
    return `${integerPart}.${decimalPart}`;
  }
  
  return integerPart;
};

const formatNumber = (num: number, isRtl: boolean): string => {
  const formatted = formatNumberWithComma(num);
  return isRtl ? toArabicNumerals(formatted) : formatted;
};

const calculatePeriodDates = (period: string) => {
  const now = new Date();
  let start: Date, end: Date;

  switch (period) {
    case 'today':
      start = addHours(startOfDay(now), 12);
      end = endOfDay(now);
      break;
    case 'yesterday':
      const yesterday = subDays(now, 1);
      start = addHours(startOfDay(yesterday), 12);
      end = endOfDay(yesterday);
      break;
    case 'week':
      start = startOfDay(subDays(now, 6));
      end = endOfDay(now);
      break;
    case 'month':
      start = startOfDay(subMonths(now, 1));
      end = endOfDay(now);
      break;
    default:
      start = addHours(startOfDay(now), 12);
      end = endOfDay(now);
  }

  return { start, end };
};

const getQuantityColor = (quantity: number): string => {
  if (quantity === 0) return 'text-gray-500';
  if (quantity <= 10) return 'text-amber-600';
  if (quantity <= 50) return 'text-emerald-600';
  if (quantity <= 100) return 'text-green-600';
  return 'text-emerald-700';
};

const getQuantityBgColor = (quantity: number): string => {
  if (quantity === 0) return 'bg-gray-50';
  if (quantity <= 10) return 'bg-amber-50';
  if (quantity <= 50) return 'bg-emerald-50';
  if (quantity <= 100) return 'bg-green-50';
  return 'bg-emerald-100';
};

// ==============================
// === PDF FONT LOADER ===
// ==============================
const loadAmiriFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  try {
    const [regularRes, boldRes] = await Promise.all([
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@master/fonts/Amiri-Regular.ttf'),
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@master/fonts/Amiri-Bold.ttf'),
    ]);
    const [regularFontBytes, boldFontBytes] = await Promise.all([
      regularRes.arrayBuffer(),
      boldRes.arrayBuffer(),
    ]);
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return window.btoa(binary);
    };
    doc.addFileToVFS(`${fontName}-normal.ttf`, arrayBufferToBase64(regularFontBytes));
    doc.addFont(`${fontName}-normal.ttf`, fontName, 'normal');
    doc.addFileToVFS(`${fontName}-bold.ttf`, arrayBufferToBase64(boldFontBytes));
    doc.addFont(`${fontName}-bold.ttf`, fontName, 'bold');
    return true;
  } catch (error) {
    console.warn('Failed to load Amiri font. Using fallback.');
    return false;
  }
};

// ==============================
// === COMPONENTS ===
// ==============================

// Search Input Component
const ProductSearchInput: React.FC<{
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  ariaLabel: string;
  isRtl: boolean;
}> = ({ value, onChange, placeholder, ariaLabel, isRtl }) => {
  return (
    <div className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pl-10 pr-3' : 'pr-10 pl-3'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white text-sm ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
        aria-label={ariaLabel}
        dir={isRtl ? 'rtl' : 'ltr'}
      />
      {value ? (
        <button
          onClick={() => onChange({ target: { value: '' } } as any)}
          className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors`}
          aria-label={isRtl ? 'مسح' : 'Clear'}
          type="button"
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Search className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
      )}
    </div>
  );
};

// Button Component
const Button: React.FC<{
  variant: 'primary' | 'outline' | 'success' | 'amber';
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
}> = ({ variant, onClick, disabled, children, icon, size = 'md' }) => {
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm'
  };
  
  const variantClasses = {
    amber: 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm hover:shadow',
    primary: 'bg-gray-800 hover:bg-gray-900 text-white shadow-sm hover:shadow',
    success: 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm hover:shadow',
    outline: 'border border-gray-200 hover:bg-gray-50 text-gray-700 hover:border-gray-300'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-2 rounded-lg font-medium transition-all ${sizeClasses[size]} ${variantClasses[variant]} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      type="button"
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="whitespace-nowrap">{children}</span>
    </button>
  );
};

// Dropdown Component
const Dropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  label?: string;
  icon?: React.ReactNode;
  isRtl: boolean;
}> = ({ value, onChange, options, ariaLabel, label, icon, isRtl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          aria-label={ariaLabel}
          type="button"
        >
          <div className="flex items-center gap-2 flex-1 truncate" dir={isRtl ? 'rtl' : 'ltr'}>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span className="truncate">{selectedOption?.label || ''}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-sm ${isRtl ? 'text-right' : 'text-left'} text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 last:border-b-0`}
                type="button"
                dir={isRtl ? 'rtl' : 'ltr'}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// MultiSelect Dropdown Component
const MultiSelectDropdown: React.FC<{
  value: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  label?: string;
  isRtl: boolean;
}> = ({ value, onChange, options, ariaLabel, label, isRtl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleOption = useCallback((optionValue: string) => {
    if (optionValue === 'all') {
      onChange(['all']);
    } else {
      const newValues = value.filter(v => v !== 'all');
      if (newValues.includes(optionValue)) {
        const filtered = newValues.filter(v => v !== optionValue);
        onChange(filtered.length === 0 ? ['all'] : filtered);
      } else {
        onChange([...newValues, optionValue]);
      }
    }
  }, [value, onChange]);

  const allSelected = value.includes('all') || value.length === 0;
  const count = value.filter(v => v !== 'all').length;
  const displayLabel = allSelected
    ? isRtl ? 'جميع الأقسام' : 'All Departments'
    : isRtl
      ? `${count} ${count === 1 ? 'قسم' : 'أقسام'}`
      : `${count} ${count === 1 ? 'Department' : 'Departments'}`;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          aria-label={ariaLabel}
          type="button"
        >
          <span className="truncate flex-1">{displayLabel}</span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => {
              const isSelected = allSelected ? opt.value === 'all' : value.includes(opt.value);
              return (
                <div 
                  key={opt.value} 
                  className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                  onClick={() => toggleOption(opt.value)}
                >
                  <span className="text-sm text-gray-700 flex-1" dir={isRtl ? 'rtl' : 'ltr'}>
                    {opt.label}
                  </span>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className="h-4 w-4 text-amber-500 rounded border-gray-300 focus:ring-amber-300 cursor-pointer"
                    readOnly
                  />
                </div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'gray' | 'emerald' | 'blue' | 'amber';
  loading?: boolean;
  isRtl: boolean;
}> = ({ title, value, icon, color, loading, isRtl }) => {
  const colors = {
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200'
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[color]} transition-all hover:shadow-sm`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between mb-3">
        <div className="p-2 rounded-lg bg-white">
          {icon}
        </div>
      </div>
      <div className="mb-2">
        <h3 className="text-sm font-medium opacity-80">{title}</h3>
      </div>
      <div className="text-xl font-bold">
        {loading ? (
          <div className="h-7 w-24 bg-gray-200 rounded animate-pulse"></div>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

// Export Dropdown Component
const ExportDropdown: React.FC<{
  onExportPDF: () => void;
  onExportExcel: () => void;
  disabled: boolean;
  isRtl: boolean;
}> = ({ onExportPDF, onExportExcel, disabled, isRtl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="amber"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        icon={<Download className="w-4 h-4" />}
        size="md"
      >
        {isRtl ? 'تصدير' : 'Export'}
      </Button>
      
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          className={`absolute z-50 mt-1.5 ${isRtl ? 'left-0' : 'right-0'} w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden`}
        >
          <button
            onClick={() => {
              onExportExcel();
              setIsOpen(false);
            }}
            disabled={disabled}
            className="w-full px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 flex items-center gap-2"
            type="button"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>Excel (.xlsx)</span>
          </button>
          <button
            onClick={() => {
              onExportPDF();
              setIsOpen(false);
            }}
            disabled={disabled}
            className="w-full px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center gap-2"
            type="button"
          >
            <FileText className="w-4 h-4" />
            <span>PDF (.pdf)</span>
          </button>
        </motion.div>
      )}
    </div>
  );
};

// ==============================
// === PDF EXPORT ===
// ==============================
const exportToPDF = async (
  data: DailySummaryData[],
  allBranches: string[],
  totals: ReportTotals,
  periodLabel: string,
  departmentsLabel: string,
  groupBy: string,
  sortBy: string,
  isRtl: boolean,
  language: string
) => {
  const toastId = 'pdf-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف PDF...' : 'Generating PDF...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const fontLoaded = await loadAmiriFont(doc);
    const fontName = fontLoaded ? 'Amiri' : 'helvetica';
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;

    const headerBgColor = [245, 158, 11]; // amber-500
    const totalRowColor = [245, 158, 11]; // amber-500
    const altRowColor = [249, 250, 251]; // gray-50
    const borderColor = [229, 231, 235]; // gray-200
    const textColor = [31, 41, 55]; // gray-800
    const lightTextColor = [107, 114, 128]; // gray-500

    // العنوان الرئيسي
    doc.setFont(fontName, 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...textColor);
    const title = isRtl ? 'تقرير ملخص الطلبات اليومية' : 'Daily Orders Summary Report';
    doc.text(title, pageWidth / 2, 15, { align: 'center' });

    // معلومات الفلاتر
    doc.setFont(fontName, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...lightTextColor);
    
    const groupByOption = GROUP_BY_OPTIONS.find(g => g.value === groupBy);
    const sortByOption = SORT_OPTIONS.find(s => s.value === sortBy);
    
    const infoParts = [];
    infoParts.push(periodLabel);
    infoParts.push(`${isRtl ? 'التجميع' : 'Group by'} ${groupByOption?.[isRtl ? 'labelAr' : 'labelEn'] || ''}`);
    infoParts.push(`${isRtl ? 'الترتيب' : 'Sort by'} ${sortByOption?.[isRtl ? 'labelAr' : 'labelEn'] || ''}`);
    
    if (departmentsLabel) {
      infoParts.push(`${isRtl ? 'الأقسام' : 'Departments'} ${departmentsLabel}`);
    }
    
    const infoText = infoParts.join('  |  ');
    doc.text(infoText, pageWidth / 2, 25, { align: 'center' });

    let yPos = 35;

    // إعداد رؤوس الأعمدة - تصحيح الترتيب
    let headers: string[];
    
    if (isRtl) {
      // للعربية: ترتيب من اليمين لليسار
      headers = [
        'إجمالي العناصر',           // أول عمود من اليمين
        ...allBranches,            // جميع الفروع
        'اسم المنتج',              // قبل الأخير
        'الكود'                    // آخر عمود من اليسار
      ];
    } else {
      // للإنجليزية: ترتيب عادي
      headers = [
        'Code',                    // أول عمود
        'Product Name',            // ثاني عمود
        ...allBranches,            // جميع الفروع
        'Total Items'              // آخر عمود
      ];
    }
    
    // تحضير بيانات الجدول - تصحيح معالجة البيانات
    const body = data.map(item => {
      if (isRtl) {
        // للعربية: ترتيب معكوس ولكن بيانات صحيحة
        const branchValues = allBranches.map(branch => item.branchQuantities[branch] || 0);
        
        return [
          formatNumberWithComma(item.totalQuantity), // إجمالي العناصر
          ...branchValues,                          // جميع الفروع
          language === 'en' && item.nameEn ? item.nameEn : item.name, // اسم المنتج
          item.code || '-'                          // الكود
        ];
      } else {
        // للإنجليزية: ترتيب عادي
        return [
          item.code || '-',                         // الكود
          language === 'en' && item.nameEn ? item.nameEn : item.name, // اسم المنتج
          ...allBranches.map(branch => item.branchQuantities[branch] || 0), // الفروع
          formatNumberWithComma(item.totalQuantity) // إجمالي العناصر
        ];
      }
    });
    
    // إضافة صف الإجماليات
    if (isRtl) {
      // حساب إجمالي كل فرع
      const totalBranchValues = allBranches.map(branch => 
        formatNumberWithComma(data.reduce((sum, item) => sum + (item.branchQuantities[branch] || 0), 0))
      );
      
      body.push([
        formatNumberWithComma(totals?.grandTotalQuantity || 0), // إجمالي العناصر
        ...totalBranchValues,                                  // إجمالي الفروع
        'الإجمالي',                                            // اسم المنتج
        ''                                                     // الكود
      ]);
    } else {
      body.push([
        '', // الكود
        'Total', // اسم المنتج
        ...allBranches.map(branch => 
          formatNumberWithComma(data.reduce((sum, item) => sum + (item.branchQuantities[branch] || 0), 0))
        ), // إجمالي الفروع
        formatNumberWithComma(totals?.grandTotalQuantity || 0) // إجمالي العناصر
      ]);
    }

    // حساب عرض الأعمدة ديناميكياً
    const columnCount = headers.length;
    const availableWidth = pageWidth - (margin * 2);
    
    // تحديد عرض الأعمدة
    const columnStyles: any = {};
    const branchCount = allBranches.length;
    
    // عرض ثابت للأعمدة الرئيسية
    const fixedWidths = {
      totalItems: 25,
      code: 25,
      productName: 50,
      branch: Math.max(15, Math.floor((availableWidth - 100) / branchCount)) // حساب عرض ديناميكي للفروع
    };
    
    if (isRtl) {
      // للعربية
      columnStyles[0] = { 
        cellWidth: fixedWidths.totalItems, 
        fontStyle: 'bold', 
        halign: 'center', 
        fillColor: [254, 243, 199], // amber-100
        valign: 'middle'
      };
      
      // أعمدة الفروع
      for (let i = 1; i <= branchCount; i++) {
        columnStyles[i] = { 
          cellWidth: fixedWidths.branch,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 7
        };
      }
      
      // عمود اسم المنتج
      columnStyles[branchCount + 1] = { 
        cellWidth: fixedWidths.productName, 
        halign: 'center', 
        fontStyle: 'bold',
        valign: 'middle'
      };
      
      // عمود الكود
      columnStyles[branchCount + 2] = { 
        cellWidth: fixedWidths.code, 
        halign: 'center', 
        fontStyle: 'bold',
        valign: 'middle'
      };
    } else {
      // للإنجليزية
      columnStyles[0] = { 
        cellWidth: fixedWidths.code, 
        fontStyle: 'bold', 
        halign: 'center',
        valign: 'middle'
      };
      
      columnStyles[1] = { 
        cellWidth: fixedWidths.productName, 
        halign: 'center', 
        fontStyle: 'bold',
        valign: 'middle'
      };
      
      // أعمدة الفروع
      for (let i = 2; i < columnCount - 1; i++) {
        columnStyles[i] = { 
          cellWidth: fixedWidths.branch,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 7
        };
      }
      
      // عمود الإجمالي
      columnStyles[columnCount - 1] = { 
        cellWidth: fixedWidths.totalItems, 
        fontStyle: 'bold', 
        halign: 'center',
        fillColor: [254, 243, 199], // amber-100
        valign: 'middle'
      };
    }

    // إنشاء الجدول
    autoTable(doc, {
      head: [headers],
      body: body,
      startY: yPos,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: {
        font: fontName,
        fontSize: 7,
        textColor: textColor,
        lineColor: borderColor,
        lineWidth: 0.1,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2,
        minCellHeight: 7,
        overflow: 'ellipsize'
      },
      headStyles: {
        fillColor: headerBgColor,
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        cellPadding: 3,
        lineWidth: 0.1,
        overflow: 'ellipsize',
        lineColor: [217, 119, 6],
        minCellHeight: 9
      },
      bodyStyles: {
        fontSize: 7,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2,
        lineWidth: 0.1,
        minCellHeight: 7,
        overflow: 'ellipsize'
      },
      alternateRowStyles: { 
        fillColor: altRowColor 
      },
      columnStyles: columnStyles,
      didParseCell: (hookData: any) => {
        // صف الإجماليات
        if (hookData.section === 'body' && hookData.row.index === hookData.table.body.length - 1) {
          hookData.cell.styles.fontStyle = 'bold';
          hookData.cell.styles.fillColor = totalRowColor;
          hookData.cell.styles.textColor = [255, 255, 255];
          hookData.cell.styles.fontSize = 8;
          hookData.cell.styles.minCellHeight = 8;
        }
        
        // تحديد مؤشرات الفروع
        let branchStartIndex, branchEndIndex;
        if (isRtl) {
          branchStartIndex = 1; // أول فرع بعد عمود الإجمالي
          branchEndIndex = branchStartIndex + branchCount; // نهاية الفروع
        } else {
          branchStartIndex = 2; // أول فرع بعد الكود واسم المنتج
          branchEndIndex = columnCount - 1; // قبل عمود الإجمالي
        }
        
        // تنسيق خلايا الفروع حسب الكمية
        if (hookData.section === 'body' && 
            hookData.column.index >= branchStartIndex && 
            hookData.column.index < branchEndIndex) {
          const value = hookData.cell.raw;
          // تحويل النص إلى رقم إذا كان تنسيق رقمي
          let numValue = value;
          if (typeof value === 'string' && value.includes(',')) {
            numValue = parseInt(value.replace(/,/g, '')) || 0;
          }
          
          if (typeof numValue === 'number' && numValue > 0) {
            if (numValue <= 10) {
              hookData.cell.styles.fillColor = [255, 251, 235];
              hookData.cell.styles.textColor = [146, 64, 14];
            } else if (numValue <= 50) {
              hookData.cell.styles.fillColor = [236, 253, 245];
              hookData.cell.styles.textColor = [6, 95, 70];
            } else if (numValue <= 100) {
              hookData.cell.styles.fillColor = [209, 250, 229];
              hookData.cell.styles.textColor = [4, 120, 87];
            } else {
              hookData.cell.styles.fillColor = [167, 243, 208];
              hookData.cell.styles.textColor = [6, 95, 70];
            }
            hookData.cell.styles.fontStyle = 'bold';
          }
        }
        
        // تحويل الأرقام إلى عربية للغة العربية
        if (isRtl && hookData.section === 'body' && typeof hookData.cell.text === 'string') {
          // تحويل الأرقام فقط (مع الفواصل)
          hookData.cell.text = hookData.cell.text.replace(/\d+/g, (match: string) => 
            toArabicNumerals(match)
          );
        }
        
        // معالجة رؤوس الأعمدة لمنع التفاف النص
        if (hookData.section === 'head') {
          hookData.cell.styles.overflow = 'ellipsize';
          hookData.cell.styles.lineHeight = 1.2;
          
          // تقليل حجم الخط للأسماء الطويلة
          const text = hookData.cell.text[0] || '';
          if (text.length > 12) {
            hookData.cell.styles.fontSize = 7;
          }
        }
      },
      willDrawCell: (data: any) => {
        // التأكد من عدم تقسيم النص
        if (data.section === 'head') {
          // لا شيء - يتم التعامل معه في didParseCell
        }
      }
    });

    // إضافة ملاحظة في الأسفل
    const finalY = (doc as any).lastAutoTable.finalY || yPos + 100;
    doc.setFontSize(7);
    doc.setTextColor(...lightTextColor);
    const note = isRtl 
      ? '* تم إنشاء هذا التقرير بواسطة نظام إدارة طلبات الجودياء'
      : '* This report was generated by aljodia Orders Management System';
    doc.text(note, pageWidth / 2, finalY + 10, { align: 'center' });

    // اسم الملف
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = isRtl 
      ? `تقرير_ملخص_الطلبات_${dateStr}.pdf`
      : `daily_orders_summary_${dateStr}.pdf`;

    // حفظ الملف
    doc.save(fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى PDF بنجاح!' : 'Exported to PDF successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('PDF Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير PDF. حاول مرة أخرى.' : 'PDF export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};
// ==============================
// === EXCEL EXPORT ===
// ==============================
const exportToExcel = async (
  data: DailySummaryData[],
  allBranches: string[],
  totals: ReportTotals,
  periodLabel: string,
  departmentsLabel: string,
  groupBy: string,
  sortBy: string,
  isRtl: boolean,
  language: string
) => {
  const toastId = 'excel-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف Excel...' : 'Generating Excel...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(isRtl ? 'الطلبات_المستلمة' : 'Received_Orders');

    // إعداد اتجاه الصفحة للغة العربية
    if (isRtl) {
      worksheet.views = [{ rightToLeft: true }];
    }

    // إضافة عنوان التقرير
    const titleRow = worksheet.addRow([isRtl ? 'تقرير الطلبات المستلمة' : 'Received Orders Report']);
    titleRow.height = 25;
    const titleCell = titleRow.getCell(1);
    titleCell.font = { size: 14, bold: true, color: { argb: '374151' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FEF3C7' } // amber-100
    };
    worksheet.mergeCells(`A1:${String.fromCharCode(65 + allBranches.length + 2)}1`);

    // إضافة معلومات التقرير في سطر واحد
    const groupByOption = GROUP_BY_OPTIONS.find(g => g.value === groupBy);
    const sortByOption = SORT_OPTIONS.find(s => s.value === sortBy);
    
    const infoParts = [];
    infoParts.push(periodLabel);
    infoParts.push(`${isRtl ? 'التجميع:' : 'Group by:'} ${groupByOption?.[isRtl ? 'labelAr' : 'labelEn'] || ''}`);
    infoParts.push(`${isRtl ? 'الترتيب:' : 'Sort by:'} ${sortByOption?.[isRtl ? 'labelAr' : 'labelEn'] || ''}`);
    
    if (departmentsLabel) {
      infoParts.push(`${isRtl ? 'الأقسام:' : 'Departments:'} ${departmentsLabel}`);
    }
    
    const infoText = infoParts.join('   -   ');
    
    const infoRow = worksheet.addRow([infoText]);
    const infoCell = infoRow.getCell(1);
    infoCell.font = { size: 10, color: { argb: '6B7280' } };
    infoCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.mergeCells(`A2:${String.fromCharCode(65 + allBranches.length + 2)}2`);

    // إضافة سطر فارغ
    worksheet.addRow([]);

    // إعداد العناوين حسب اللغة
    let headers: string[];
    if (isRtl) {
      headers = ['الكود', 'اسم المنتج', ...allBranches, 'إجمالي الطلبات'];
    } else {
      headers = ['Code', 'Product Name', ...allBranches, 'Total Orders'];
    }

    const headerRow = worksheet.addRow(headers);
    headerRow.height = 25;
    
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F59E0B' } // amber-500
      };
      cell.alignment = { 
        horizontal: 'center', 
        vertical: 'middle',
        wrapText: true
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'D97706' } },
        left: { style: 'thin', color: { argb: 'D97706' } },
        bottom: { style: 'thin', color: { argb: 'D97706' } },
        right: { style: 'thin', color: { argb: 'D97706' } }
      };
    });

    // إضافة البيانات - مع دعم الكسور
    data.forEach((item, rowIndex) => {
      const rowData = [
        item.code || '-',
        language === 'en' && item.nameEn ? item.nameEn : item.name,
        ...allBranches.map(branch => item.branchQuantities[branch] || 0),
        item.totalQuantity
      ];
      
      const row = worksheet.addRow(rowData);
      row.height = 20;
      
      row.eachCell((cell, colNumber) => {
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle',
          wrapText: true
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'E5E7EB' } },
          left: { style: 'thin', color: { argb: 'E5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
          right: { style: 'thin', color: { argb: 'E5E7EB' } }
        };
        
        // صفوف متناوبة
        if (rowIndex % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F9FAFB' }
          };
        }
        
        // تنسيق خلايا الفروع (أعمدة الأرقام)
        if (colNumber > 2 && colNumber <= headers.length - 1) {
          // تنسيق الأرقام لدعم الكسور
          cell.numFmt = '#,##0.00';
          
          const quantity = cell.value as number || 0;
          
          // إضافة تنسيق مشروط للألوان
          if (quantity > 0) {
            if (quantity <= 10) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FEF3C7' } // amber-100
              };
              cell.font = { color: { argb: '92400E' } }; // amber-800
            } else if (quantity <= 50) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'D1FAE5' } // emerald-100
              };
              cell.font = { color: { argb: '065F46' } }; // emerald-800
            } else if (quantity <= 100) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'A7F3D0' } // emerald-200
              };
              cell.font = { color: { argb: '047857' }, bold: true }; // emerald-700
            } else {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '86EFAC' } // emerald-300
              };
              cell.font = { color: { argb: '065F46' }, bold: true }; // emerald-800
            }
          }
        }
      });
    });

    // إضافة صف الإجماليات
    const totalRowData = [
      isRtl ? 'الإجمالي' : 'Total',
      '',
      ...allBranches.map(branch => 
        data.reduce((sum, item) => sum + (item.branchQuantities[branch] || 0), 0)
      ),
      totals.grandTotalQuantity
    ];
    
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.height = 25;
    
    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F59E0B' } // amber-500
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'D97706' } },
        left: { style: 'thin', color: { argb: 'D97706' } },
        bottom: { style: 'thin', color: { argb: 'D97706' } },
        right: { style: 'thin', color: { argb: 'D97706' } }
      };
      
      // تنسيق الأرقام في صف الإجماليات لدعم الكسور
      if (colNumber > 2) {
        cell.numFmt = '#,##0.00';
      }
    });

    // ضبط عرض الأعمدة
    worksheet.columns.forEach((column, index) => {
      if (index === 0) { // عمود الكود
        column.width = 15;
      } else if (index === 1) { // عمود اسم المنتج
        column.width = 35;
      } else if (index <= headers.length - 2) { // أعمدة الفروع
        column.width = 25; // زيادة العرض لدعم الكسور
      } else if (index === headers.length - 1) { // عمود الكمية الإجمالية
        column.width = 20;
      }
      
      column.alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' };
    });

    // اسم الملف بناءً على الفلاتر
    const groupByText = groupByOption?.[isRtl ? 'labelAr' : 'labelEn'] || '';
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = isRtl 
      ? `تقرير_الطلبات_${groupByText}_${dateStr}.xlsx`
      : `orders_report_${groupBy}_${dateStr}.xlsx`;

    // حفظ الملف
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى Excel بنجاح!' : 'Exported to Excel successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Excel Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير Excel. حاول مرة أخرى.' : 'Excel export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === MAIN COMPONENT ===
// ==============================
const DailyOrdersSummary: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRtl = language === 'ar';
  
  // States
  const [reportData, setReportData] = useState<DailySummaryData[]>([]);
  const [quickStats, setQuickStats] = useState<QuickStats | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [period, setPeriod] = useState('today');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [groupBy, setGroupBy] = useState('product');
  const [sortBy, setSortBy] = useState('code_asc');
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [totals, setTotals] = useState<ReportTotals>({
    grandTotalQuantity: 0,
    totalProducts: 0,
    totalBranches: 0,
    totalOrders: 0,
    totalReceivedOrders: 0
  });
  const [branchData, setBranchData] = useState<BranchData>({});
  const [allBranches, setAllBranches] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [expandedBranches, setExpandedBranches] = useState<number>(6);

  // Memoized values
  const periodOptions = useMemo(
    () => PERIOD_OPTIONS.map(p => ({
      value: p.value,
      label: isRtl ? p.labelAr : p.labelEn
    })),
    [isRtl]
  );

  const groupByOptions = useMemo(
    () => GROUP_BY_OPTIONS.map(g => ({
      value: g.value,
      label: isRtl ? g.labelAr : g.labelEn
    })),
    [isRtl]
  );

  const sortOptions = useMemo(
    () => SORT_OPTIONS.map(s => ({
      value: s.value,
      label: isRtl ? s.labelAr : s.labelEn
    })),
    [isRtl]
  );

  const departmentOptions = useMemo(() => {
    const baseOptions = departments.map(d => ({
      value: d._id,
      label: isRtl ? d.name : (d.nameEn || d.name),
    })).sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 'all', label: isRtl ? 'جميع الأقسام' : 'All Departments' }, ...baseOptions];
  }, [departments, isRtl]);

  // Search debounce
  const debouncedSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
    },
    []
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      debouncedSearch(searchInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput, debouncedSearch]);

  // Fetch data functions
  const fetchBranches = useCallback(async () => {
    try {
      const res = await branchesAPI.getAll();
      const branchesData = Array.isArray(res) 
        ? res.map(b => ({
            _id: b._id,
            name: b.name,
            nameEn: b.nameEn,
            code: b.code,
            displayName: isRtl ? b.name : (b.nameEn || b.name),
          }))
        : (res.data || []).map((b: any) => ({
            _id: b._id,
            name: b.name,
            nameEn: b.nameEn,
            code: b.code,
            displayName: isRtl ? b.name : (b.nameEn || b.name),
          }));
      setBranches(branchesData);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  }, [isRtl]);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await departmentAPI.getAll({ limit: 100 });
      const departmentsData = Array.isArray(res) ? res : (res.data || []);
      setDepartments(departmentsData);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);

  const fetchQuickStats = useCallback(async () => {
    if (!user) return;
    
    setStatsLoading(true);
    try {
      const params: any = { 
        lang: language 
      };
      
      if (period === 'custom' && startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      } else {
        const { start, end } = calculatePeriodDates(period);
        params.startDate = start.toISOString().split('T')[0];
        params.endDate = end.toISOString().split('T')[0];
      }
      
      const response = await reportsAPI.getQuickStats(params);
      if (response.data) {
        setQuickStats({
          totalOrders: response.data.totalOrders || 0,
          receivedOrders: response.data.receivedOrders || 0,
          totalQuantity: response.data.totalQuantity || 0,
          receivedQuantity: response.data.receivedQuantity || 0,
          period: period
        });
      }
    } catch (error) {
      console.error('Error fetching quick stats:', error);
      toast.error(isRtl ? 'فشل جلب الإحصائيات' : 'Failed to load statistics');
    } finally {
      setStatsLoading(false);
    }
  }, [user, period, startDate, endDate, isRtl, language]);

  const fetchDailySummary = useCallback(async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'production')) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { start, end } = calculatePeriodDates(period);
      const currentStartDate = startDate || start.toISOString().split('T')[0];
      const currentEndDate = endDate || end.toISOString().split('T')[0];

      const params: any = {
        startDate: currentStartDate,
        endDate: currentEndDate,
        groupBy: groupBy,
        lang: language,
        type: 'received'
      };

      if (!selectedDepartments.includes('all') && selectedDepartments.length > 0) {
        params.department = selectedDepartments.filter(d => d !== 'all').join(',');
      }

      const response = await reportsAPI.getDailySummary(params);
      
      if (response.data && Array.isArray(response.data)) {
        const data = response.data;
        
        // تحويل البيانات
        const formattedData = data.map((item: any, index: number) => {
          const displayName = language === 'en' && item.nameEn ? item.nameEn : (item.name || `Product ${index + 1}`);
          
          // معالجة بيانات الفروع
          let branchQuantities = {};
          if (item.branchQuantities && typeof item.branchQuantities === 'object') {
            branchQuantities = item.branchQuantities;
          } else if (item.branchAnalysis && typeof item.branchAnalysis === 'object') {
            branchQuantities = Object.fromEntries(
              Object.entries(item.branchAnalysis).map(([branch, data]: [string, any]) => [
                branch, 
                data.receivedQuantity || data.quantity || 0
              ])
            );
          }
          
          return {
            id: item.id || item._id || `item-${index}`,
            code: item.code || item.productCode || `P${String(index + 1).padStart(3, '0')}`,
            name: displayName,
            nameAr: item.name || displayName,
            nameEn: item.nameEn || displayName,
            totalQuantity: item.receivedQuantity || item.totalQuantity || 0,
            branchQuantities: branchQuantities,
            productCode: item.productCode || item.code || `P${String(index + 1).padStart(3, '0')}`,
            orderCount: item.orderCount || 0,
            receivedQuantity: item.receivedQuantity || item.totalQuantity || 0
          };
        });
        
        setReportData(formattedData);
        
        // جمع بيانات الفروع
        const branchDataMap: BranchData = {};
        const uniqueBranches = new Set<string>();
        
        formattedData.forEach(item => {
          Object.entries(item.branchQuantities || {}).forEach(([branch, quantity]) => {
            if (branch && branch.trim() !== '') {
              uniqueBranches.add(branch);
              if (!branchDataMap[branch]) {
                branchDataMap[branch] = 0;
              }
              branchDataMap[branch] += quantity as number;
            }
          });
        });
        
        setBranchData(branchDataMap);
        setAllBranches(Array.from(uniqueBranches).sort());
        
        // حساب الإجماليات
        const grandTotalQuantity = formattedData.reduce((sum, item) => sum + item.totalQuantity, 0);
        const totalOrders = formattedData.reduce((sum, item) => sum + (item.orderCount || 0), 0);
        
        setTotals({
          grandTotalQuantity,
          totalProducts: formattedData.length,
          totalBranches: uniqueBranches.size,
          totalOrders,
          totalReceivedOrders: grandTotalQuantity
        });
        
      } else {
        // بيانات تجريبية تحتوي على كسور
        const sampleData: DailySummaryData[] = [
          {
            id: '1',
            code: 'P001',
            name: language === 'en' ? 'Chicken Shawarma' : 'شاورما دجاج',
            nameAr: 'شاورما دجاج',
            nameEn: 'Chicken Shawarma',
            totalQuantity: 120.5,
            branchQuantities: {
              [language === 'en' ? 'Main Branch' : 'الفرع الرئيسي']: 45.25,
              [language === 'en' ? 'Riyadh Branch' : 'فرع الرياض']: 75.25
            },
            productCode: 'P001',
            orderCount: 45,
            receivedQuantity: 120.5
          },
          {
            id: '2',
            code: 'P002',
            name: language === 'en' ? 'Beef Burger' : 'برجر لحم',
            nameAr: 'برجر لحم',
            nameEn: 'Beef Burger',
            totalQuantity: 180.75,
            branchQuantities: {
              [language === 'en' ? 'Main Branch' : 'الفرع الرئيسي']: 65.5,
              [language === 'en' ? 'Riyadh Branch' : 'فرع الرياض']: 115.25
            },
            productCode: 'P002',
            orderCount: 65,
            receivedQuantity: 180.75
          }
        ];
        
        const sampleBranches = [
          language === 'en' ? 'Main Branch' : 'الفرع الرئيسي',
          language === 'en' ? 'Riyadh Branch' : 'فرع الرياض'
        ];
        
        const sampleBranchData: BranchData = {
          [sampleBranches[0]]: 110.75,
          [sampleBranches[1]]: 190.5
        };
        
        setReportData(sampleData);
        setBranchData(sampleBranchData);
        setAllBranches(sampleBranches);
        setTotals({
          grandTotalQuantity: 301.25,
          totalProducts: 2,
          totalBranches: 2,
          totalOrders: 110,
          totalReceivedOrders: 301.25
        });
      }
      
    } catch (error: any) {
      console.error('Error fetching report:', error);
      toast.error(isRtl ? 'فشل جلب التقرير' : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [user, period, startDate, endDate, groupBy, selectedDepartments, language]);

  // Initial data fetch
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'production')) {
      Promise.all([
        fetchBranches(),
        fetchDepartments(),
        fetchQuickStats(),
        fetchDailySummary()
      ]);
    }
  }, [user, fetchBranches, fetchDepartments, fetchQuickStats, fetchDailySummary]);

  // Fetch data when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (user && (user.role === 'admin' || user.role === 'production')) {
        fetchDailySummary();
        fetchQuickStats();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [period, groupBy, selectedDepartments, language, user, fetchDailySummary, fetchQuickStats]);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let data = [...reportData];
    
    // Filter by search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter(item => 
        item.name?.toLowerCase().includes(searchLower) ||
        item.code?.toLowerCase().includes(searchLower) ||
        item.productCode?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort data
    data.sort((a, b) => {
      switch (sortBy) {
        case 'code_asc':
          return (a.code || '').localeCompare(b.code || '');
        case 'code_desc':
          return (b.code || '').localeCompare(a.code || '');
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'quantity_desc':
          return b.totalQuantity - a.totalQuantity;
        case 'quantity_asc':
          return a.totalQuantity - b.totalQuantity;
        default:
          return (a.code || '').localeCompare(b.code || '');
      }
    });
    
    return data;
  }, [reportData, searchTerm, sortBy]);

  // Generate period label
  const periodLabel = useMemo(() => {
    const locale = isRtl ? arSA : enUS;
    
    if (period === 'custom') {
      if (startDate && endDate && isSameDay(parseISO(startDate), parseISO(endDate))) {
        return isRtl 
          ? `${format(parseISO(startDate), 'EEEE d/M/yyyy', { locale })}`
          : `${format(parseISO(startDate), 'EEEE d/M/yyyy', { locale })}`;
      }
      return isRtl
        ? `${format(parseISO(startDate || new Date().toISOString()), 'd/M/yyyy', { locale })} - ${format(parseISO(endDate || new Date().toISOString()), 'd/M/yyyy', { locale })}`
        : `${format(parseISO(startDate || new Date().toISOString()), 'd/M/yyyy', { locale })} - ${format(parseISO(endDate || new Date().toISOString()), 'd/M/yyyy', { locale })}`;
    }
    
    const { start, end } = calculatePeriodDates(period);
    if (isSameDay(start, end)) {
      return isRtl
        ? `${format(start, 'EEEE d/M/yyyy', { locale })}`
        : `${format(start, 'EEEE d/M/yyyy', { locale })}`;
    }
    return isRtl
      ? `${format(start, 'd/M/yyyy', { locale })} - ${format(end, 'd/M/yyyy', { locale })}`
      : `${format(start, 'd/M/yyyy', { locale })} - ${format(end, 'd/M/yyyy', { locale })}`;
  }, [period, startDate, endDate, isRtl]);

  // Departments label
  const departmentsLabel = useMemo(() => {
    if (selectedDepartments.includes('all')) return '';
    const selectedNames = selectedDepartments
      .filter(v => v !== 'all')
      .map(id => {
        const dept = departments.find(d => d._id === id);
        return language === 'en' && dept?.nameEn ? dept.nameEn : dept?.name || '';
      })
      .filter(Boolean);
    return selectedNames.join(' - ');
  }, [selectedDepartments, departments, language]);

  // Event handlers
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setPeriod('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setPeriod('custom');
  };

  const handleRefresh = () => {
    fetchDailySummary();
    fetchQuickStats();
    toast.success(isRtl ? 'تم تحديث البيانات' : 'Data refreshed successfully');
  };

  const handleExportPDF = () => {
    exportToPDF(
      filteredData,
      allBranches,
      totals,
      periodLabel,
      departmentsLabel,
      groupBy,
      sortBy,
      isRtl,
      language
    );
  };

  const handleExportExcel = () => {
    exportToExcel(
      filteredData,
      allBranches,
      totals,
      periodLabel,
      departmentsLabel,
      groupBy,
      sortBy,
      isRtl,
      language
    );
  };

  const toggleExpandBranches = () => {
    setExpandedBranches(expandedBranches === allBranches.length ? 6 : allBranches.length);
  };

  if (!user || (user.role !== 'admin' && user.role !== 'production')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
            <X className="w-4 h-4 text-red-600" />
          </div>
          <span className="text-sm text-red-600">{isRtl ? 'غير مصرح لك للوصول إلى هذه الصفحة' : 'Not authorized to access this page'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6 mx-auto max-w-[99vw]" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="w-full mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center  md:items-start  gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl shadow-lg hidden md:flex">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold md:text-right text-center text-gray-900">
                {isRtl ? 'ملخص الطلبات  ' : 'orders summery '}
              </h1>
              <p className="text-sm text-gray-600 md:text-right text-center mt-1">
                {isRtl ? 'نظرة عامة على ملخص الطلبات  من الفروع' : 'Overview of orders summery from branches'}
              </p>
            </div>
          </div>
          
          {/* Header Actions */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              icon={<RefreshCw className="w-4 h-4" />}
              size="md"
            >
              {isRtl ? 'تحديث' : 'Refresh'}
            </Button>
            
            <ExportDropdown
              onExportPDF={handleExportPDF}
              onExportExcel={handleExportExcel}
              disabled={filteredData.length === 0}
              isRtl={isRtl}
            />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatsCard
            title={isRtl ? 'إجمالي الطلبات' : 'Total Orders'}
            value={statsLoading ? '...' : formatNumber(quickStats?.totalOrders || totals.totalOrders, isRtl)}
            icon={<Package className="w-5 h-5" />}
            color="gray"
            loading={statsLoading}
            isRtl={isRtl}
          />
          <StatsCard
            title={isRtl ? 'طلبات مستلمة' : 'Received Orders'}
            value={statsLoading ? '...' : formatNumber(quickStats?.receivedOrders || totals.totalOrders, isRtl)}
            icon={<CheckCircle className="w-5 h-5" />}
            color="emerald"
            loading={statsLoading}
            isRtl={isRtl}
          />
          <StatsCard
            title={isRtl ? 'عدد المنتجات' : 'Products Count'}
            value={statsLoading ? '...' : formatNumber(totals.totalProducts, isRtl)}
            icon={<Database className="w-5 h-5" />}
            color="amber"
            loading={statsLoading}
            isRtl={isRtl}
          />
          <StatsCard
            title={isRtl ? 'إجمالي العناصر' : 'Total Items'}
            value={statsLoading ? '...' : formatNumber(quickStats?.receivedQuantity || totals.grandTotalQuantity, isRtl)}
            icon={<Hash className="w-5 h-5" />}
            color="blue"
            loading={statsLoading}
            isRtl={isRtl}
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            {isRtl ? 'الفلاتر والبحث' : 'Filters & Search'}
          </h3>
          
          {/* Search and Period */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRtl ? 'بحث في المنتجات' : 'Search Products'}
              </label>
              <ProductSearchInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={isRtl ? 'ابحث بكود المنتج أو الاسم...' : 'Search by product code or name...'}
                ariaLabel={isRtl ? 'بحث' : 'Search'}
                isRtl={isRtl}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRtl ? 'الفترة' : 'Period'}
              </label>
              <Dropdown
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                ariaLabel={isRtl ? 'اختر الفترة' : 'Select period'}
                icon={<Calendar className="w-4 h-4" />}
                isRtl={isRtl}
              />
            </div>
          </div>

          {/* 3 dropdowns in one row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Departments MultiSelect */}
            <MultiSelectDropdown
              value={selectedDepartments}
              onChange={setSelectedDepartments}
              options={departmentOptions}
              ariaLabel={isRtl ? 'اختر الأقسام' : 'Select departments'}
              label={isRtl ? 'الأقسام' : 'Departments'}
              isRtl={isRtl}
            />

            {/* Sort */}
            <Dropdown
              value={sortBy}
              onChange={setSortBy}
              options={sortOptions}
              ariaLabel={isRtl ? 'اختر طريقة الترتيب' : 'Select sorting method'}
              label={isRtl ? 'الترتيب' : 'Sort By'}
              icon={<SortAsc className="w-4 h-4" />}
              isRtl={isRtl}
            />

            {/* Group By */}
            <Dropdown
              value={groupBy}
              onChange={setGroupBy}
              options={groupByOptions}
              ariaLabel={isRtl ? 'اختر طريقة التجميع' : 'Select grouping method'}
              label={isRtl ? 'التجميع' : 'Group By'}
              icon={<Grid className="w-4 h-4" />}
              isRtl={isRtl}
            />
          </div>

          {/* Custom Dates */}
          {period === 'custom' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {isRtl ? 'من تاريخ' : 'From Date'}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  className="w-full py-2 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {isRtl ? 'إلى تاريخ' : 'To Date'}
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  className="w-full py-2 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Report Header Info */}
      {!loading && filteredData.length > 0 && (
        <div className="mb-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex flex-wrap justify-center items-center gap-4 text-sm">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-amber-200">
              <Calendar className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-gray-700">{periodLabel}</span>
            </div>
            
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-amber-200">
              <Grid className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-gray-700">
                {isRtl ? 'التجميع: ' : 'Group by: '}
                {groupByOptions.find(g => g.value === groupBy)?.label || ''}
              </span>
            </div>
            
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-amber-200">
              <SortAsc className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-gray-700">
                {isRtl ? 'الترتيب: ' : 'Sort by: '}
                {sortOptions.find(s => s.value === sortBy)?.label || ''}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-sm text-gray-600">{isRtl ? 'جارٍ تحميل البيانات...' : 'Loading data...'}</p>
          </div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="inline-block p-3 bg-amber-100 rounded-full mb-3">
            <Search className="w-6 h-6 text-amber-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-700 mb-2">{isRtl ? 'لا توجد بيانات' : 'No data found'}</h3>
          <p className="text-gray-600 text-sm mb-4">
            {isRtl 
              ? 'لا توجد بيانات مطابقة لبحثك.'
              : 'No data matches your search.'
            }
          </p>
          <button
            variant="amber"
            onClick={handleRefresh}
            icon={<RefreshCw className="w-4 h-4" />}
            size="sm"
            className='py-1.5 px-3 text-xs text-white mx-auto flex bg-amber-600 rounded-lg '
          >
            {isRtl ? 'تحديث البيانات' : 'Refresh Data'}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-amber-50/50">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-700">
                  {isRtl ? 'تفاصيل التقرير' : 'Report Details'}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {isRtl ? 'عرض ' : 'Showing '}{formatNumber(filteredData.length, isRtl)} {isRtl ? 'منتج' : 'products'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto max-w-full">
            <table className="min-w-full w-full text-xs border-collapse" dir={isRtl ? 'rtl' : 'ltr'}>
              <thead>
                <tr className="bg-amber-50 border-b border-amber-200">
                  {/* الترتيب حسب اللغة - للعربية من اليمين لليسار */}
                  <th className="px-4 py-3 font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[90px]">
                    {isRtl ? 'الكود' : 'Code'}
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[200px]">
                    {isRtl ? 'اسم المنتج' : 'Product Name'}
                  </th>
                  
                  {/* أعمدة الفروع - مع إمكانية التوسيع */}
                  {allBranches.slice(0, expandedBranches).map((branch) => (
                    <th 
                      key={branch} 
                      className="px-3 py-3 font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[140px] max-w-[160px]"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-xs font-medium truncate max-w-[130px]" title={branch}>
                          {branch}
                        </span>
                      </div>
                    </th>
                  ))}
                  
                  {/* زر عرض/إخفاء الفروع الإضافية */}
                  {allBranches.length > 6 && (
                    <th className="px-3 py-3 text-center border-l border-amber-200">
                      <button
                        onClick={toggleExpandBranches}
                        className="flex items-center justify-center gap-1 text-amber-600 hover:text-amber-700 text-xs font-medium"
                      >
                        {expandedBranches === allBranches.length ? (
                          <>
                            <ChevronUp className="w-3 h-3" />
                            {isRtl ? 'إخفاء' : 'Show Less'}
                          </>
                        ) : (
                          <>
                            <ChevronRight className="w-3 h-3" />
                            {isRtl ? `+${allBranches.length - 6}` : `+${allBranches.length - 6}`}
                          </>
                        )}
                      </button>
                    </th>
                  )}
                  
                  {/* إجمالي الطلبات */}
                  <th className="px-4 py-3 font-medium text-gray-700 text-center whitespace-nowrap min-w-[140px] bg-amber-100">
                    {isRtl ? 'إجمالي الطلبات' : 'Total Orders'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={`transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                  >
                    {/* خلايا الكود واسم المنتج */}
                    <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200">
                      <span className="font-mono bg-gray-100 px-3 py-1.5 rounded text-xs font-medium text-gray-700">
                        {row.code || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200">
                      <span className="font-medium text-gray-900 text-sm">
                        {language === 'en' && row.nameEn ? row.nameEn : row.name}
                      </span>
                    </td>
                    
                    {/* خلايا الفروع */}
                    {allBranches.slice(0, expandedBranches).map((branch) => {
                      const quantity = row.branchQuantities[branch] || 0;
                      const colorClass = getQuantityColor(quantity);
                      const bgClass = getQuantityBgColor(quantity);
                      
                      return (
                        <td 
                          key={branch} 
                          className={`px-3 py-3 text-center border-l border-gray-200 ${bgClass} ${colorClass}`}
                        >
                          <div className="flex items-center justify-center h-full">
                            <span className={`font-medium text-sm ${quantity === 0 ? 'text-gray-400' : ''}`}>
                              {quantity === 0 ? '-' : formatNumber(quantity, isRtl)}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    
                    {/* خلية فارغة إذا كان هناك زر التوسيع */}
                    {allBranches.length > 6 && (
                      <td className="px-3 py-3 text-center border-l border-gray-200 bg-gray-50"></td>
                    )}
                    
                    {/* خلية إجمالي الطلبات */}
                    <td className="px-4 py-3 text-gray-800 text-center bg-amber-50">
                      <span className="font-bold text-amber-700 text-sm">{formatNumber(row.totalQuantity, isRtl)}</span>
                    </td>
                  </tr>
                ))}
                
                {/* صف الإجماليات */}
                <tr className="bg-amber-100 font-bold border-t-2 border-amber-300">
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300">
                    {isRtl ? 'الإجمالي' : 'Total'}
                  </td>
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300">
                    -
                  </td>
                  
                  {/* إجماليات الفروع */}
                  {allBranches.slice(0, expandedBranches).map((branch) => (
                    <td key={branch} className="px-3 py-3 text-gray-800 text-center border-l border-amber-300">
                      {formatNumber(branchData[branch] || 0, isRtl)}
                    </td>
                  ))}
                  
                  {/* خلية فارغة إذا كان هناك زر التوسيع */}
                  {allBranches.length > 6 && (
                    <td className="px-3 py-3 text-center border-l border-amber-300"></td>
                  )}
                  
                  {/* الإجمالي الكلي */}
                  <td className="px-4 py-3 text-gray-800 text-center bg-amber-200">
                    <span className="text-amber-800 font-bold text-sm">{formatNumber(totals.grandTotalQuantity || 0, isRtl)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer Summary */}
      {!loading && filteredData.length > 0 && (
        <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            <div className="text-xs text-gray-600">
              {isRtl 
                ? `عرض ${formatNumber(filteredData.length, isRtl)} من ${formatNumber(reportData.length, isRtl)} منتج`
                : `Showing ${formatNumber(filteredData.length, isRtl)} of ${formatNumber(reportData.length, isRtl)} products`
              }
            </div>
            <div className="flex flex-wrap gap-4">
              
              <div className="text-xs text-gray-700 font-medium flex i0tems-center gap-1">
                <Hash className="w-3 h-3 text-gray-600" />
                {isRtl ? 'العناصر: ' : 'Items: '} 
                <span className="font-bold text-amber-700">{formatNumber(totals.grandTotalQuantity || 0, isRtl)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyOrdersSummary;